import { useMemo } from "react";
import type { AdminUser } from "../../api/adminUsers";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { AdminUserTariffCell } from "../AdminUserTariffCell/AdminUserTariffCell";
import { AdminUserLimitsBlock } from "../AdminUserLimitsBlock/AdminUserLimitsBlock";
import { AdminUserSitesCell } from "../AdminUserSitesCell/AdminUserSitesCell";
import { RequestTablePagination } from "../RequestTable/RequestTablePagination";
import {
  sortAdminUsers,
  type AdminUserSortDirection,
  type AdminUserSortField,
} from "../../utils/adminUserSort";
import { hasRole } from "../../utils/authRoles";
import "./AdminUsersTable.scss";

interface AdminUsersTableProps {
  data: AdminUser[];
  page: number;
  pageSize: number;
  totalPages: number;
  showArchived: boolean;
  sortField: AdminUserSortField;
  sortDirection: AdminUserSortDirection;
  actingUserId: number | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onGrantAdmin: (userId: number) => void;
  onRevokeAdmin: (userId: number) => void;
  onUserSelect: (user: AdminUser) => void;
}

const formatRegistrationDate = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const AdminUsersTable = ({
  data,
  page,
  pageSize,
  totalPages,
  showArchived,
  sortField,
  sortDirection,
  actingUserId,
  onPageChange,
  onPageSizeChange,
  onGrantAdmin,
  onRevokeAdmin,
  onUserSelect,
}: AdminUsersTableProps) => {
  const sortedData = useMemo(
    () => sortAdminUsers(data, sortField, sortDirection),
    [data, sortField, sortDirection]
  );
  const canGoNext = page + 1 < totalPages;
  const archivedColumnCount = showArchived ? 1 : 0;
  const columnCount = 8 + archivedColumnCount;

  return (
    <div className="request-table-wrapper admin-users-table-wrapper">
      <div className="request-table-card">
        <table className="request-table admin-users-table">
          <thead>
            <tr>
              <th>Имя</th>
              <th>Email</th>
              <th>Регистрация</th>
              <th>Лимиты / тарифы</th>
              <th>Заявки</th>
              <th className="admin-users-table-sites-col">Сайты</th>
              {showArchived ? <th>Архивные тарифы</th> : null}
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="request-table-empty">
                  Нет пользователей
                </td>
              </tr>
            ) : (
              sortedData.map((user) => {
                const isAdmin = hasRole({ roles: user.roles }, "ADMIN");
                const isActing = actingUserId === user.id;
                const displayName = user.alias?.trim() || user.email?.trim() || "—";

                return (
                  <tr
                    key={user.id}
                    className="admin-users-table-row-clickable"
                    onClick={() => onUserSelect(user)}
                  >
                    <td>
                      <div className="admin-users-table-user-cell">
                        <span className="admin-users-table-user-name">{displayName}</span>
                        {user.locked ? (
                          <span className="admin-users-table-status admin-users-table-status--locked">
                            Заблокирован
                          </span>
                        ) : null}
                        {isAdmin ? (
                          <span className="admin-users-table-status admin-users-table-status--admin">
                            Админ
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="admin-users-table-email-cell">
                        <span>{user.email?.trim() || "—"}</span>
                        {user.emailConfirmed === false ? (
                          <span className="admin-users-table-status">Email не подтверждён</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="admin-users-table-date">{formatRegistrationDate(user.createdAt)}</td>
                    <td>
                      {user.userLimits ? (
                        <AdminUserLimitsBlock limits={user.userLimits} />
                      ) : (
                        <AdminUserTariffCell tariffs={user.activeTariffs} mode="active" />
                      )}
                    </td>
                    <td>
                      <AdminUserTariffCell tariffs={user.requestedTariffs} mode="requested" />
                    </td>
                    <td className="admin-users-table-sites-col" onClick={(event) => event.stopPropagation()}>
                      <AdminUserSitesCell sites={user.requestedFor} userLabel={displayName} />
                    </td>
                    {showArchived ? (
                      <td>
                        <AdminUserTariffCell tariffs={user.archivedTariffs ?? []} mode="archived" />
                      </td>
                    ) : null}
                    <td className="admin-users-table-actions" onClick={(event) => event.stopPropagation()}>
                      <div className="admin-users-table-actions-stack">
                        {isAdmin ? (
                          <button
                            type="button"
                            className="btn admin-users-table-action admin-users-table-action--danger"
                            disabled={isActing}
                            onClick={() => onRevokeAdmin(user.id)}
                          >
                            {isActing ? <CircularProgress size="sm" /> : "Снять права администратора"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary admin-users-table-action"
                            disabled={isActing}
                            onClick={() => onGrantAdmin(user.id)}
                          >
                            {isActing ? <CircularProgress size="sm" /> : "Выдать права администратора"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <RequestTablePagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        canGoNext={canGoNext}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};
